/**
 * Business Unit Detail Route
 *
 * Displays comprehensive business unit metadata
 */

import { useState } from 'react';
import { createFileRoute, useNavigate, Outlet, useLocation } from '@tanstack/react-router';
import { useBusinessUnit } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import {
  DetailSkeleton,
  EmptyState,
  DeleteConfirmationDialog,
  PermissionGate,
} from '../../components/shared';
import { deleteBusinessUnit, ApiError } from '../../lib/apiClient';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/divider';
import './businessUnitId.css';

export const Route = createFileRoute('/business-units/$businessUnitId')({
  component: BusinessUnitDetailPage,
});

function BusinessUnitDetailPage(): JSX.Element {
  const { businessUnitId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If on a child route (edit), render only the outlet
  const isChildRoute = location.pathname.endsWith('/edit');

  // Fetch business unit data
  const { data, loading, error: fetchError } = useBusinessUnit(businessUnitId);
  const businessUnit = data?.businessUnit;

  const CardElement = 'forge-card' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const DividerElement = 'forge-divider' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  if (isChildRoute) {
    return <Outlet />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="business-unit-detail-page">
        <DetailSkeleton cards={4} />
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="business-unit-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load business unit"
          description={fetchError.message}
          actionLabel="Back to Business Units"
          onAction={() => navigate({ to: '/business-units', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  // Not found state
  if (!businessUnit) {
    return (
      <div className="business-unit-detail-page">
        <EmptyState
          icon="search_off"
          title="Business unit not found"
          description={`No business unit found with ID: ${businessUnitId}`}
          actionLabel="Back to Business Units"
          onAction={() => navigate({ to: '/business-units', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  // Format dates
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle edit action
  const handleEdit = (): void => {
    navigate({
      to: `/business-units/${businessUnit.entityId}/edit` as string,
    });
  };

  // Handle delete action
  const handleDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);

    try {
      // Soft delete by default
      await deleteBusinessUnit(businessUnitId, false);

      // Refetch GraphQL data to update cache
      await apolloClient.refetchQueries({
        include: ['ListBusinessUnits'],
      });

      // Navigate back to business units list
      navigate({ to: '/business-units', search: { page: 1, pageSize: 25 } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsDeleting(false);
    }
  };

  return (
    <div className="business-unit-detail-page">
      {/* Header */}
      <div className="business-unit-detail-header">
        <div>
          <ForgeButton
            type="button"
            variant="text"
            onClick={() => navigate({ to: '/business-units', search: { page: 1, pageSize: 25 } })}
          >
            <IconElement name="arrow_back" aria-hidden="true" />
            Back to Business Units
          </ForgeButton>
          <h1 className="forge-typography--headline4">{businessUnit.name}</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {businessUnit.description || 'No description provided'}
          </p>
        </div>
        <div className="business-unit-detail-actions">
          <PermissionGate permission="business-unit:update">
            <ForgeButton type="button" variant="raised" onClick={handleEdit}>
              <IconElement name="edit" aria-hidden="true" />
              Edit
            </ForgeButton>
          </PermissionGate>
          <PermissionGate permission="business-unit:delete">
            <ForgeButton type="button" variant="outlined" onClick={handleDelete}>
              <IconElement name="delete" aria-hidden="true" />
              Delete
            </ForgeButton>
          </PermissionGate>
        </div>
      </div>

      <div className="business-unit-detail-content">
        {/* Overview Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Overview</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Business Unit ID</span>
              <span className="detail-value">{businessUnit.entityId}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Unit Type</span>
              <span className="detail-value">{businessUnit.unitType}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Lifecycle Status</span>
              <span className="detail-value">{businessUnit.lifecycleStatus || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Code</span>
              <span className="detail-value">{businessUnit.code || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Hierarchy Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Hierarchy</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Parent Business Unit</span>
              <span className="detail-value">{businessUnit.parentBusinessUnitId || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Additional Information Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Additional Information</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Tags</span>
              <span className="detail-value">
                {businessUnit.tags && businessUnit.tags.length > 0
                  ? businessUnit.tags.join(', ')
                  : '—'}
              </span>
            </div>
          </div>
        </CardElement>

        {/* Metadata Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Metadata</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Created</span>
              <span className="detail-value">
                {formatDate(businessUnit._createdAt)} by {businessUnit._createdBy}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Last Updated</span>
              <span className="detail-value">
                {formatDate(businessUnit._updatedAt)} by {businessUnit._updatedBy}
              </span>
            </div>
          </div>
        </CardElement>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteDialogOpen(false)}
          title="Delete Business Unit"
          message="Are you sure you want to delete this business unit?"
          warning="This action will soft delete the business unit. It can be recovered by an administrator."
          itemName={businessUnit.name}
          isDeleting={isDeleting}
        />
      )}

      {/* Error Banner */}
      {error && <div className="detail-error-toast">{error}</div>}
    </div>
  );
}
